import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogHeader,
  ResponsiveDialogTitle, ResponsiveDialogFooter, ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChoiceChips } from "@/components/ui/choice-chips";
import { MagnifyingGlass, Check } from "@phosphor-icons/react";
import {
  ICON_GROUPS, CATEGORY_PALETTE, DEFAULT_ICON, DEFAULT_COLOR, getIcon,
} from "@/lib/categoryIcons";

const KIND_OPTIONS = [
  { value: "expense", label: "Expense", activeClassName: "bg-expense text-white", testid: "category-kind-expense" },
  { value: "income", label: "Income", activeClassName: "bg-income text-black", testid: "category-kind-income" },
];

const emptyForm = (kind) => ({
  name: "",
  kind,
  icon: DEFAULT_ICON,
  color: CATEGORY_PALETTE[0],
});

/** Grid of every icon, filtered by a free-text query against the icon name. */
function IconPicker({ value, color, query, onPick }) {
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return ICON_GROUPS.map((g) => ({
      label: g.label,
      names: Object.keys(g.icons).filter(
        (n) => !needle || n.toLowerCase().includes(needle),
      ),
    })).filter((g) => g.names.length);
  }, [query]);

  if (!groups.length) {
    return (
      <p className="text-xs text-muted-foreground text-center py-6">
        No icons match “{query}”.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
            {group.label}
          </p>
          <div className="grid grid-cols-8 gap-1">
            {group.names.map((name) => {
              const Icon = getIcon(name);
              const active = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  aria-label={name}
                  aria-pressed={active}
                  data-testid={`icon-option-${name}`}
                  onClick={() => onPick(name)}
                  className={`aspect-square rounded-md flex items-center justify-center transition-colors ${
                    active
                      ? "bg-secondary ring-1 ring-inset"
                      : "hover:bg-secondary/60 text-muted-foreground"
                  }`}
                  style={active ? { color, boxShadow: `inset 0 0 0 1px ${color}` } : undefined}
                >
                  <Icon size={17} weight="bold" />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Create or edit a category.
 *
 * `kind` is fixed once a category exists: its transactions are income or
 * expense rows, and flipping the kind would orphan every one of them. Editing
 * therefore shows the kind as a read-only label rather than a control.
 */
export default function CategoryDialog({ open, onOpenChange, editing, defaultKind = "expense", onSaved }) {
  const [form, setForm] = useState(emptyForm(defaultKind));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setForm(
      editing
        ? {
            name: editing.name,
            kind: editing.kind,
            icon: editing.icon || DEFAULT_ICON,
            color: editing.color || DEFAULT_COLOR,
          }
        : emptyForm(defaultKind),
    );
  }, [open, editing, defaultKind]);

  const Preview = getIcon(form.icon);
  const usage = editing?.transaction_count || 0;

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Enter a category name");
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/categories/${editing.id}`, {
          name, icon: form.icon, color: form.color,
        });
        const renamed = res.data?.transactions_renamed || 0;
        toast.success(
          renamed
            ? `Category updated — ${renamed} transaction${renamed === 1 ? "" : "s"} moved to “${name}”`
            : "Category updated",
        );
      } else {
        await api.post("/categories", {
          name, kind: form.kind, icon: form.icon, color: form.color,
        });
        toast.success(`“${name}” added`);
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="bg-card border-border sm:max-w-md" data-testid="category-dialog">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-heading tracking-tight">
            {editing ? "Edit category" : "New category"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="text-muted-foreground">
            {editing
              ? "Renaming updates every transaction already filed under it."
              : "Pick a name, an icon and a colour. It shows up straight away when you add a transaction."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-4">
          {/* Live preview + name */}
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center"
              style={{ background: `${form.color}1f`, color: form.color }}
              data-testid="category-preview"
            >
              <Preview size={22} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input
                data-testid="category-name-input"
                autoFocus
                maxLength={40}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="e.g. Groceries"
                className="mt-1 bg-background"
              />
            </div>
          </div>

          {/* Kind */}
          <div>
            <Label className="text-xs text-muted-foreground">Applies to</Label>
            {editing ? (
              <p className="mt-1 text-sm capitalize text-muted-foreground">
                {editing.kind} transactions{" "}
                <span className="text-xs">(cannot be changed)</span>
              </p>
            ) : (
              <ChoiceChips
                label="Applies to"
                variant="segmented"
                options={KIND_OPTIONS}
                value={form.kind}
                onValueChange={(kind) => setForm((f) => ({ ...f, kind }))}
                className="mt-1"
              />
            )}
          </div>

          {/* Colour */}
          <div>
            <Label className="text-xs text-muted-foreground">Colour</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {CATEGORY_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={c}
                  aria-pressed={form.color === c}
                  data-testid={`color-option-${c.replace("#", "")}`}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  className="w-7 h-7 rounded-md flex items-center justify-center"
                  style={{ background: c }}
                >
                  {form.color === c && <Check size={14} weight="bold" className="text-black" />}
                </button>
              ))}
            </div>
          </div>

          {/* Icon */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs text-muted-foreground">Icon</Label>
              <div className="relative w-40">
                <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="icon-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search icons"
                  // iOS zooms the page into any field under 16px, so the
                  // compact size is for a mouse only.
                  className="pl-7 h-8 text-xs bg-background touch:h-10 touch:text-base"
                />
              </div>
            </div>
            <ScrollArea className="h-48 mt-2 rounded-md border border-border p-2">
              <IconPicker
                value={form.icon}
                color={form.color}
                query={query}
                onPick={(icon) => setForm((f) => ({ ...f, icon }))}
              />
            </ScrollArea>
          </div>

          {editing && usage > 0 && (
            <p className="text-xs text-muted-foreground">
              {usage} transaction{usage === 1 ? " uses" : "s use"} this category.
            </p>
          )}
        </div>

        <ResponsiveDialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="category-cancel-btn">
            Cancel
          </Button>
          <Button
            data-testid="category-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-income text-black hover:bg-income/90 font-semibold"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Add category"}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
